import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';

export interface SummaryCards {
  currentRate: number;
  previousRate: number;
  eligible: number;
  compliant: number;
  nonCompliant: number;
  notEnoughData: number;
  target: number;
}

export interface LabelData {
  label: string;
}

export interface ValueData {
  value: number;
}

export interface PieData {
  value: number;
  color: string;
}

export interface PieLegend {
  label: string;
  color: string;
}

export interface PieChartdata {
  data: PieData[];
  legend: PieLegend[];
}

export interface PrimaryCareCharts {
  legend: PieLegend[];
  eligible: PieData[];
  compliant: PieData[];
  nonCompliant: PieData[];
}

export interface MemberDistributionGender {
  legend: PieLegend[];
  male: PieData[];
  female: PieData[];
}

export const formatSummaryCards = (data: any) => {
  const summaryCards: SummaryCards = {
    currentRate: data.currentRate ?? 0,
    previousRate: data.previousRate ?? 0,
    eligible: data.eligibleCount ?? 0,
    compliant: data.measureMetCount ?? 0,
    nonCompliant: data.measureNotMetCount ?? 0,
    target: data.nationalScore ?? 0,
    notEnoughData: data?.notEnoughData ?? 0,
  };

  return summaryCards;
};

export const formatTopServiceType = (data: any) => {
  const chartData: PieChartdata = {
    data: [],
    legend: [],
  };

  const colors: string[] = [
    '#FFDBA5',
    '#FF8C8C',
    '#9CB6FD',
    patientAccessTheme.palette.primary[100] || '#CFDCFF',
    patientAccessTheme.palette.primary[300] || '#475CB0',
  ];
  for (let i = 0; i < data.length; i++) {
    chartData.data.push({
      value: data[i]?.members,
      color: colors[i],
    });
    chartData.legend.push({
      label: data[i]?.type,
      color: colors[i],
    });
  }

  return chartData;
};

export const formatPrimaryCareDistribution = (data: any) => {
  const primaryCareYes = data?.find(
    (value: any) => value?.primary_care === 'YES',
  );
  const primaryCareNo = data?.find(
    (value: any) => value?.primary_care === 'NO',
  );

  const pieChartsData: PrimaryCareCharts = {
    legend: [
      {
        label: 'YES',
        color: patientAccessTheme.palette.primary[300] || '#475CB0',
      },
      {
        label: 'NO',
        color: patientAccessTheme.palette.primary[100] || '#CFDCFF',
      },
    ],
    eligible: [
      {
        value: primaryCareYes?.compliant + primaryCareYes?.notCompliant || 0,
        color: patientAccessTheme.palette.primary[300] || '#475CB0',
      },
      {
        value: primaryCareNo?.compliant + primaryCareNo?.notCompliant || 0,
        color: patientAccessTheme.palette.primary[100] || '#CFDCFF',
      },
    ],
    compliant: [
      {
        value: primaryCareYes?.compliant || 0,
        color: patientAccessTheme.palette.primary[300] || '#475CB0',
      },
      {
        value: primaryCareNo?.compliant || 0,
        color: patientAccessTheme.palette.primary[100] || '#CFDCFF',
      },
    ],
    nonCompliant: [
      {
        value: primaryCareYes?.notCompliant || 0,
        color: patientAccessTheme.palette.primary[300] || '#475CB0',
      },
      {
        value: primaryCareNo?.notCompliant || 0,
        color: patientAccessTheme.palette.primary[100] || '#CFDCFF',
      },
    ],
  };

  return pieChartsData;
};

export const getHCCFlag = (HCC: any, subMeasure: string) => {
  let hccFlag: 'High' | 'Low' | 'Medium' | ' ' | '' = 'Low';

  if (!HCC) {
    return hccFlag;
  }

  if (subMeasure === 'Diabetes') {
    hccFlag = HCC['Diabetes'];
  } else if (subMeasure === 'Cardiovascular Conditions') {
    hccFlag = HCC['Cardiovascular_Conditions'];
  } else if (subMeasure === 'Respiratory Conditions') {
    hccFlag = HCC['Respiratory_Conditions'];
  } else if (subMeasure === 'Musculo Skeletal') {
    hccFlag = HCC['Musculo_Skeletal'];
  } else if (subMeasure === 'Behavioral Health') {
    hccFlag = HCC['Behavioral_Health'];
  } else if (subMeasure === 'Prevention And Screening') {
    hccFlag = HCC['Prevention_and_Screening'];
  }

  return hccFlag === ' ' || hccFlag === '' ? 'Low' : hccFlag;
};

export const formatMemberDistributionRisk = (data: any) => {
  const chartData: any = {
    dataSet: [
      {
        seriesname: 'Compliant',
        plottooltext: 'Compliant - $dataValue',
        data: [],
        color: patientAccessTheme.palette.primary[400],
      },
      {
        seriesname: 'Non-Compliant',
        plottooltext: 'Non-Compliant - $dataValue',
        data: [],
        color: patientAccessTheme.palette.primary[100],
      },
      {
        seriesname: 'Not Enough Data',
        plottooltext: 'Not Enough Data - $dataValue',
        data: [],
        color: '#87A6FB',
      },
    ],
    labels: [],
  };
  for (const value of data) {
    chartData.labels.push({
      label: value?.age_group,
    });
    chartData.dataSet[0].data.push({
      value: value?.hcc_flags_met,
    });
    chartData.dataSet[1].data.push({
      value: value?.hcc_flags_notmet,
    });
    chartData.dataSet[2].data.push({
      value: value?.hcc_flags_notenoughdata,
    });
  }

  return chartData;
};

export const formatMemberDistributionAge = (data: any) => {
  const chartData: any = {
    dataSet: [
      {
        seriesname: 'Compliant',
        plottooltext: 'Compliant - $dataValue',
        data: [],
        color: patientAccessTheme.palette.primary[400],
      },
      {
        seriesname: 'Non-Compliant',
        plottooltext: 'Non-Compliant - $dataValue',
        data: [],
        color: patientAccessTheme.palette.primary[100],
      },
      {
        seriesname: 'Not Enough Data',
        plottooltext: 'Not Enough Data - $dataValue',
        data: [],
        color: '#87A6FB',
      },
    ],
    labels: [],
  };
  for (const value of data) {
    chartData.labels.push({
      label: value?.age,
    });
    chartData.dataSet[0].data.push({
      value: value?.compliant,
    });
    chartData.dataSet[1].data.push({
      value: value?.non_compliant,
    });
    chartData.dataSet[2].data.push({
      value: value?.not_enough_data,
    });
  }

  return chartData;
};

export const formatMemberDistributionGender = (data: any) => {
  const genderF = data?.find((value: any) => value?.gender === 'F');
  const genderM = data?.find((value: any) => value?.gender === 'M');

  const pieChartsData: MemberDistributionGender = {
    legend: [
      {
        label: 'Compliant',
        color: patientAccessTheme.palette.primary[100] || '#CFDCFF',
      },
      {
        label: 'Non-Compliant',
        color: patientAccessTheme.palette.primary[300] || '#475CB0',
      },
      {
        label: 'Not Enough Data',
        color: '#87A6FB',
      },
    ],
    male: [
      {
        value: genderM?.compliant,
        color: patientAccessTheme.palette.primary[100] || '#CFDCFF',
      },
      {
        value: genderM?.non_compliant,
        color: patientAccessTheme.palette.primary[300] || '#475CB0',
      },
      {
        value: genderM?.not_enough_data,
        color: '#87A6FB',
      },
    ],
    female: [
      {
        value: genderF?.compliant,
        color: patientAccessTheme.palette.primary[100] || '#CFDCFF',
      },
      {
        value: genderF?.non_compliant,
        color: patientAccessTheme.palette.primary[300] || '#475CB0',
      },
      {
        value: genderF?.not_enough_data,
        color: '#87A6FB',
      },
    ],
  };

  return pieChartsData;
};
